import React, { useState, useEffect, useRef } from 'react';
import { useAppSelector } from '../../hooks/useAppSelector';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { showNotification } from '../../store/slices/notificationPopupSlice';
import { API_ENDPOINTS, API_BASE_URL } from '../../config/api';
import axios from '../../config/axiosConfig';
import WaiterHome from '../../components/waiter/WaiterHome';
import WaiterAttendance from '../../components/waiter/WaiterAttendance';
import WaiterTableManagement from '../../components/waiter/WaiterTableManagement';
import WaiterOrderManagement from '../../components/waiter/WaiterOrderManagement';
import WaiterLiveOrders from '../../components/waiter/WaiterLiveOrders';
import WaiterPerformance from '../../components/waiter/WaiterPerformance';
import WaiterNotifications from '../../components/waiter/WaiterNotifications';
import Layout from '../../components/Layout';
import Toast from '../../components/Toast';
import { normalizeRestaurantId } from '../../utils/restaurantUtils';
import { playNotificationSound } from '../../utils/notificationSound';

const WaiterDashboard = () => {
    const dispatch = useAppDispatch();
    const { user, restaurant } = useAppSelector(state => state.auth);
    const waiterId = user?.staffId || user?.id;
    // Normalize restaurantId to ensure it's always a string
    const restaurantId = normalizeRestaurantId(user?.restaurantId || restaurant?._id || restaurant);
    
    const [activeTab, setActiveTab] = useState('home');
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [homeRefreshKey, setHomeRefreshKey] = useState(0);
    const [pendingNotificationsCount, setPendingNotificationsCount] = useState(0);
    const lastNotificationCountRef = useRef(0);

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    };


    // Helper: filter active requests from the last 24 hours
    const getRecentActiveCount = (requests = []) => {
        const now = Date.now();
        const oneDayMs = 24 * 60 * 60 * 1000;
        return requests.filter((req) => {
            if (!req) return false;
            if (req.status === 'done') return false;
            if (!req.createdAt) return true;
            const createdTime = new Date(req.createdAt).getTime();
            return now - createdTime < oneDayMs;
        }).length;
    };

    // Fetch pending notifications count (only non-done requests in last 24 hours)
    useEffect(() => {
        if (waiterId && restaurantId) {
            const fetchNotificationCount = async () => {
                try {
                    const response = await axios.get(
                        API_ENDPOINTS.WAITER_REQUESTS_BY_WAITER(waiterId, restaurantId)
                    );
                    const newCount = getRecentActiveCount(response.data || []);

                    // Show pop-up (with sound) if notification count increased (new notification received)
                    if (newCount > lastNotificationCountRef.current && lastNotificationCountRef.current > 0) {
                        playNotificationSound();
                        dispatch(showNotification({
                            title: 'New Request',
                            message: `You have ${newCount - lastNotificationCountRef.current} new waiter request(s).`,
                        }));
                    }

                    setPendingNotificationsCount(newCount);
                    lastNotificationCountRef.current = newCount;
                } catch (error) {
                    console.error('Failed to fetch notification count:', error);
                }
            };
            fetchNotificationCount();
            // Poll every 10 seconds for new notifications
            const interval = setInterval(fetchNotificationCount, 10000);
            return () => clearInterval(interval);
        }
    }, [waiterId, restaurantId, dispatch]);

    const tabs = [
        { id: 'home', label: 'Home', icon: '🏠' },
        { id: 'notifications', label: 'Notifications', icon: '🔔' },
        { id: 'attendance', label: 'Attendance', icon: '📅' },
        { id: 'tables', label: 'Tables', icon: '🪑' },
        { id: 'orders', label: 'Orders', icon: '📋' },
        { id: 'live', label: 'Live Orders', icon: '🔴' },
        { id: 'performance', label: 'Performance', icon: '📊' }
    ];

    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
        // Refresh home stats when navigating back to home tab
        if (tabId === 'home') {
            setHomeRefreshKey(prev => prev + 1);
        }
    };

    const renderContent = () => {
        switch (activeTab) {
            case 'home':
                return <WaiterHome key={homeRefreshKey} waiterId={waiterId} restaurantId={restaurantId} onNavigate={handleTabChange} />;
            case 'notifications':
                return <WaiterNotifications waiterId={waiterId} restaurantId={restaurantId} />;
            case 'attendance':
                return <WaiterAttendance waiterId={waiterId} restaurantId={restaurantId} />;
            case 'tables':
                return <WaiterTableManagement waiterId={waiterId} restaurantId={restaurantId} onNavigate={handleTabChange} />;
            case 'orders':
                return <WaiterOrderManagement waiterId={waiterId} restaurantId={restaurantId} />;
            case 'live':
                return <WaiterLiveOrders waiterId={waiterId} restaurantId={restaurantId} />;
            case 'performance':
                return <WaiterPerformance waiterId={waiterId} restaurantId={restaurantId} />;
            default:
                return <WaiterHome key={homeRefreshKey} waiterId={waiterId} restaurantId={restaurantId} onNavigate={handleTabChange} />;
        }
    };

    return (
        <Layout>
            <div className="container mx-auto lg:mt-5 px-2 sm:px-4 lg:px-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 gap-3">
                    <h1 className="text-2xl sm:text-3xl font-bold">Waiter Dashboard</h1>
                    <div className="text-sm text-gray-600">
                        Welcome, {user?.name || 'Waiter'}
                    </div>
                </div>

                {/* Tab Navigation */}
                <div className="bg-white rounded-lg shadow-md mb-4 sm:mb-6">
                    <div className="flex flex-wrap border-b overflow-x-auto">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => handleTabChange(tab.id)}
                                className={`flex items-center space-x-1 sm:space-x-2 px-3 sm:px-6 py-3 sm:py-4 font-medium transition-all duration-300 whitespace-nowrap min-w-0 flex-shrink-0 relative ${
                                    activeTab === tab.id
                                        ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                                        : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                                }`}
                            >
                                <span className="text-base sm:text-lg">{tab.icon}</span>
                                <span className="text-sm sm:text-base hidden sm:inline">{tab.label}</span>
                                {tab.id === 'notifications' && pendingNotificationsCount > 0 && (
                                    <span className="absolute top-1 right-1 sm:top-2 sm:right-2 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                                        {pendingNotificationsCount > 9 ? '9+' : pendingNotificationsCount}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                    
                    <div className="p-3 sm:p-6">
                        {renderContent()}
                    </div>
                </div>
            </div>

            {toast.show && <Toast message={toast.message} type={toast.type} />}
        </Layout>
    );
};

export default WaiterDashboard;

